import { useState, useEffect, useCallback } from "react";
import { apiGetMessages, apiUpdateMessageStatus, apiDeleteMessage, ApiMessage } from "@/utils/apiClient";

type MsgStatus = "new" | "read" | "replied";
type FilterType = "Tümü" | "Yeni" | "Okundu" | "Yanıtlandı";

const statusStyle: Record<MsgStatus, string> = {
  new: "bg-[#3B82F6]/20 text-[#B8C5D8] border border-[#3B82F6]/30",
  read: "bg-white/10 text-[#7B8FAF] border border-white/10",
  replied: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
};
const statusLabel: Record<MsgStatus, string> = {
  new: "Yeni",
  read: "Okundu",
  replied: "Yanıtlandı",
};

function Spinner() {
  return (
    <div className="flex flex-col items-center justify-center py-24">
      <i className="ri-loader-4-line animate-spin text-3xl text-[#3B82F6]"></i>
      <p className="text-[#7B8FAF] text-sm mt-3">Mesajlar yükleniyor...</p>
    </div>
  );
}

export default function MessagesManager() {
  const [msgs, setMsgs] = useState<ApiMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<FilterType>("Tümü");
  const [selected, setSelected] = useState<ApiMessage | null>(null);
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const filters: FilterType[] = ["Tümü", "Yeni", "Okundu", "Yanıtlandı"];
  const filterMap: Record<FilterType, MsgStatus | null> = { Tümü: null, Yeni: "new", Okundu: "read", Yanıtlandı: "replied" };

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiGetMessages();
      setMsgs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mesajlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
    // 30 saniyede bir otomatik yenile (canlı takip)
    const interval = setInterval(loadMessages, 30_000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  const filtered = msgs.filter((m) => {
    const matchFilter = filter === "Tümü" || m.status === filterMap[filter];
    const q = search.toLowerCase();
    const matchSearch =
      m.name.toLowerCase().includes(q) ||
      m.subject.toLowerCase().includes(q) ||
      m.project.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  const markAs = async (id: number, status: MsgStatus) => {
    // Optimistic update
    setMsgs((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
    if (selected?.id === id) setSelected((prev) => (prev ? { ...prev, status } : null));
    try {
      await apiUpdateMessageStatus(id, status);
    } catch {
      // rollback
      loadMessages();
    }
  };

  const deleteMsg = async (id: number) => {
    setDeletingId(id);
    try {
      await apiDeleteMessage(id);
      setMsgs((prev) => prev.filter((m) => m.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Silme işlemi başarısız");
    } finally {
      setDeletingId(null);
    }
  };

  const openMessage = (msg: ApiMessage) => {
    if (msg.status === "new") markAs(msg.id, "read");
    setSelected(msg);
  };

  const newCount = msgs.filter((m) => m.status === "new").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            Mesajlar / Formlar
            {newCount > 0 && (
              <span className="ml-3 text-base bg-[#3B82F6] text-white px-2.5 py-0.5 rounded-full">{newCount} yeni</span>
            )}
          </h1>
          <p className="text-[#7B8FAF] text-sm mt-0.5">
            {loading ? "Yükleniyor..." : `${msgs.length} toplam mesaj · otomatik yenileme açık`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-[#0F1729] border border-[#2B3654] rounded-lg px-3 py-2 w-full sm:w-64">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-[#5E7398] text-sm"></i>
            </div>
            <input
              type="text"
              placeholder="Mesaj ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent text-white text-sm outline-none placeholder-[#5E7398] flex-1"
            />
          </div>
          <button
            onClick={loadMessages}
            disabled={loading}
            title="Yenile"
            className="w-9 h-9 flex items-center justify-center border border-[#2B3654] text-[#7B8FAF] rounded-lg hover:border-[#3B82F6] hover:text-white transition-all cursor-pointer flex-shrink-0 disabled:opacity-40"
          >
            <i className={`ri-refresh-line text-base ${loading ? "animate-spin" : ""}`}></i>
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-lg">
          <i className="ri-error-warning-line flex-shrink-0"></i>
          {error}
          <button onClick={loadMessages} className="ml-auto underline text-xs cursor-pointer">Yeniden dene</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
              filter === f
                ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                : "bg-transparent text-[#7B8FAF] border-[#2B3654] hover:text-white hover:border-[#3B82F6]/50"
            }`}
          >
            {f}
            {f === "Yeni" && newCount > 0 && (
              <span className="ml-1.5 bg-white/20 px-1.5 rounded-full">{newCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading && msgs.length === 0 ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Message List */}
          <div className="lg:col-span-2 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
            {filtered.length === 0 ? (
              <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-10 text-center">
                <i className="ri-inbox-line text-4xl text-[#5E7398]"></i>
                <p className="text-[#7B8FAF] text-sm mt-3">
                  {search ? "Arama sonucu bulunamadı" : "Henüz mesaj yok"}
                </p>
              </div>
            ) : (
              filtered.map((msg) => (
                <div
                  key={msg.id}
                  onClick={() => openMessage(msg)}
                  className={`bg-[#141E35] border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                    selected?.id === msg.id
                      ? "border-[#3B82F6] bg-[#3B82F6]/10"
                      : "border-[#2B3654] hover:border-[#3B82F6]/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {msg.status === "new" && (
                          <span className="w-2 h-2 rounded-full bg-[#3B82F6] flex-shrink-0 animate-pulse"></span>
                        )}
                        <p className="text-white text-sm font-semibold truncate">{msg.name}</p>
                      </div>
                      <p className="text-[#A5B5CC] text-xs truncate">{msg.subject}</p>
                      <p className="text-[#5E7398] text-xs mt-1">{msg.email} · {msg.date}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${statusStyle[msg.status as MsgStatus]}`}>
                      {statusLabel[msg.status as MsgStatus]}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-3">
            {selected ? (
              <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6 sticky top-4">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h3 className="text-white font-semibold text-base">{selected.subject}</h3>
                    <p className="text-[#7B8FAF] text-xs mt-1">{selected.date}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full ${statusStyle[selected.status as MsgStatus]}`}>
                      {statusLabel[selected.status as MsgStatus]}
                    </span>
                    <button
                      onClick={() => { if (window.confirm("Bu mesajı silmek istediğinize emin misiniz?")) deleteMsg(selected.id); }}
                      disabled={deletingId === selected.id}
                      className="w-7 h-7 flex items-center justify-center text-[#5E7398] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all cursor-pointer"
                      title="Mesajı Sil"
                    >
                      <i className={deletingId === selected.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}></i>
                    </button>
                  </div>
                </div>

                <div className="bg-[#0F1729] rounded-xl p-4 mb-5">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-[#7B8FAF] text-xs mb-0.5">Gönderen</p>
                      <p className="text-white text-sm font-medium">{selected.name}</p>
                    </div>
                    <div>
                      <p className="text-[#7B8FAF] text-xs mb-0.5">Tarih</p>
                      <p className="text-white text-sm">{selected.date}</p>
                    </div>
                    <div>
                      <p className="text-[#7B8FAF] text-xs mb-0.5">E-posta</p>
                      <p className="text-[#B8C5D8] text-sm break-all">{selected.email}</p>
                    </div>
                    <div>
                      <p className="text-[#7B8FAF] text-xs mb-0.5">Telefon</p>
                      <p className="text-[#B8C5D8] text-sm">{selected.phone || "—"}</p>
                    </div>
                    {selected.project && selected.project !== "—" && (
                      <div className="col-span-2">
                        <p className="text-[#7B8FAF] text-xs mb-0.5">Konu</p>
                        <p className="text-[#B8C5D8] text-sm">{selected.project}</p>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-[#1E2B4A] pt-4">
                    <p className="text-[#7B8FAF] text-xs mb-2">Mesaj</p>
                    <p className="text-[#A5B5CC] text-sm leading-relaxed whitespace-pre-wrap">{selected.message || "—"}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <a
                    href={`mailto:${selected.email}`}
                    className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white text-sm rounded-lg hover:bg-[#3d4e6a] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-send-line text-sm"></i>
                    </div>
                    E-posta Gönder
                  </a>
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-2 px-4 py-2 border border-[#2B3654] text-[#A5B5CC] text-sm rounded-lg hover:border-[#3B82F6] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-phone-line text-sm"></i>
                      </div>
                      Ara
                    </a>
                  )}
                  {selected.status !== "replied" && (
                    <button
                      onClick={() => markAs(selected.id, "replied")}
                      className="flex items-center gap-2 px-4 py-2 border border-emerald-500/30 text-emerald-400 text-sm rounded-lg hover:bg-emerald-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-double-line text-sm"></i>
                      </div>
                      Yanıtlandı İşaretle
                    </button>
                  )}
                  {selected.status === "replied" && (
                    <button
                      onClick={() => markAs(selected.id, "new")}
                      className="flex items-center gap-2 px-4 py-2 border border-[#2B3654] text-[#7B8FAF] text-sm rounded-lg hover:border-[#3B82F6] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-restart-line text-sm"></i>
                      </div>
                      Yeni Olarak İşaretle
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-16 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-[#0F1729] rounded-xl">
                  <i className="ri-mail-open-line text-3xl text-[#3B82F6]"></i>
                </div>
                <p className="text-white font-medium">Mesaj seçin</p>
                <p className="text-[#7B8FAF] text-sm mt-1">Detayları görmek için soldan bir mesaj seçin</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
